import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import EventMap from "@/components/EventMap";
import AdBanner from "@/components/AdBanner";
import { useLang } from "@/context/LanguageContext";
import { CATEGORY_META, CATEGORY_KEYS } from "@/lib/categories";
import { COUNTRY_LABELS } from "@/i18n/translations";
import { isNearFullMoon } from "@/lib/moon";
import { Search, SlidersHorizontal, List, Map as MapIcon, X, Moon, Music } from "lucide-react";

const COUNTRIES = ["CH", "FR", "DE", "IT", "AT"];
const SABBATS = ["imbolc", "ostara", "beltane", "litha", "lughnasadh", "mabon", "samhain", "yule"];

export default function Events({ initialView = "list" }) {
  const { lang, t, tCategory, tCountry } = useLang();
  const [view, setView] = useState(initialView);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [canton, setCanton] = useState("");
  const [country, setCountry] = useState("");
  const [maxDistance, setMaxDistance] = useState(100);
  const [dateFrom, setDateFrom] = useState("");
  const [fullMoonOnly, setFullMoonOnly] = useState(false);
  const [sabbat, setSabbat] = useState("");

  const { data: cantons = [] } = useQuery({ queryKey: ["cantons"], queryFn: async () => (await api.get("/cantons")).data });

  const params = useMemo(() => {
    const p = { limit: 200 };
    if (q) p.q = q;
    if (category) p.category = category;
    if (canton) p.canton = canton;
    if (country) p.country = country;
    if (maxDistance < 100) p.max_distance = maxDistance;
    if (dateFrom) p.date_from = new Date(dateFrom).toISOString();
    return p;
  }, [q, category, canton, country, maxDistance, dateFrom]);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events", params],
    queryFn: async () => (await api.get("/events", { params })).data,
  });

  const reset = () => { setQ(""); setCategory(""); setCanton(""); setCountry(""); setMaxDistance(100); setDateFrom(""); setFullMoonOnly(false); setSabbat(""); };
  const hasFilters = q || category || canton || country || maxDistance < 100 || dateFrom || fullMoonOnly || sabbat;
  let shown = events;
  if (fullMoonOnly) shown = shown.filter((e) => e.start_date && isNearFullMoon(new Date(e.start_date), 2));
  if (sabbat) shown = shown.filter((e) => e.sabbat === sabbat);

  const selCls = "w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "ItemList", name: t("nav_events"),
    itemListElement: shown.slice(0, 100).map((e, i) => ({
      "@type": "ListItem", position: i + 1, name: e.title, url: `${origin}/events/${e.slug}`,
    })),
  };

  return (
    <>
      <Seo title={t("nav_events")} description={t("hero_subtitle")} canonicalPath="/events" jsonLd={jsonLd} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 border-b border-amber-900/30 pb-5">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">swiss-pagan.ch</div>
            <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("nav_events")}</h1>
          </div>
          <div className="flex items-center gap-2 bg-[#121418] border border-[#272A34] rounded-lg p-1">
            <button data-testid="view-toggle-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm transition-all ${view === "list" ? "bg-amber-600 text-stone-950 font-semibold" : "text-stone-300 hover:text-amber-400"}`}><List className="w-4 h-4" /> {t("toggle_list")}</button>
            <button data-testid="view-toggle-map" onClick={() => setView("map")} className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm transition-all ${view === "map" ? "bg-amber-600 text-stone-950 font-semibold" : "text-stone-300 hover:text-amber-400"}`}><MapIcon className="w-4 h-4" /> {t("toggle_map")}</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="agenda-quicklinks">
          <Link to="/map" data-testid="agenda-link-map" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm bg-[#121418] border border-[#272A34] text-stone-300 hover:text-amber-400 hover:border-amber-500/40 transition-all"><MapIcon className="w-4 h-4 text-amber-500" /> {t("nav_map")}</Link>
          <Link to="/concerts" data-testid="agenda-link-concerts" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm bg-[#121418] border border-[#272A34] text-stone-300 hover:text-amber-400 hover:border-amber-500/40 transition-all"><Music className="w-4 h-4 text-amber-500" /> {t("nav_concerts")}</Link>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* FILTERS */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-serif text-lg text-stone-200"><SlidersHorizontal className="w-4 h-4 text-amber-500" /> {t("filters")}</div>
              {hasFilters && <button data-testid="filter-reset" onClick={reset} className="text-xs font-mono text-amber-500 hover:text-amber-400 flex items-center gap-1"><X className="w-3 h-3" />{t("filter_reset")}</button>}
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("filter_search")} className={`${selCls} pl-9`} />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("filter_category")}</label>
              <select data-testid="filter-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={selCls}>
                <option value="">{t("filter_all")}</option>
                {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{tCategory(c)}</option>)}
              </select>
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("filter_canton")}</label>
              <select data-testid="filter-canton-select" value={canton} onChange={(e) => setCanton(e.target.value)} className={selCls}>
                <option value="">{t("filter_all")}</option>
                {cantons.map((c) => <option key={c.key} value={c.key}>{c.key} ({c.count})</option>)}
              </select>
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("filter_country")}</label>
              <select data-testid="filter-country-select" value={country} onChange={(e) => setCountry(e.target.value)} className={selCls}>
                <option value="">{t("filter_all")}</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{tCountry(c)}</option>)}
              </select>
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("field_start")}</label>
              <input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={selCls} />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("filter_sabbat")}</label>
              <select data-testid="filter-sabbat-select" value={sabbat} onChange={(e) => setSabbat(e.target.value)} className={selCls}>
                <option value="">{t("filter_all")}</option>
                {SABBATS.map((s) => <option key={s} value={s}>{t(`sabbat_${s}`)}</option>)}
              </select>
            </div>

            <button
              data-testid="filter-fullmoon"
              onClick={() => setFullMoonOnly((v) => !v)}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm border transition-all ${fullMoonOnly ? "bg-amber-600 text-stone-950 border-amber-400 font-semibold" : "bg-[#181A20] text-stone-300 border-[#272A34] hover:border-amber-500/40"}`}
            >
              <Moon className="w-4 h-4" /> {t("filter_fullmoon")}
            </button>

            <div>
              <label className="flex items-center justify-between font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">
                <span>{t("filter_distance")}</span>
                <span className="text-amber-400">{maxDistance} km</span>
              </label>
              <input data-testid="filter-distance-slider" type="range" min="0" max="100" step="5" value={maxDistance} onChange={(e) => setMaxDistance(Number(e.target.value))} className="w-full accent-amber-600" />
            </div>

            <div className="pt-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase bg-amber-950/50 text-amber-300 border border-amber-800/40" data-testid="results-count">
                {shown.length} {t("results_count")}
              </div>
            </div>
          </aside>

          {/* RESULTS */}
          <div>
            <AdBanner placement="events" className="mb-6" />
            {view === "map" ? (
              <EventMap events={shown} height="640px" />
            ) : isLoading ? (
              <div className="grid sm:grid-cols-2 gap-6">{[...Array(4)].map((_, i) => <div key={i} className="h-72 bg-[#121418] border border-[#272A34] rounded-lg animate-pulse" />)}</div>
            ) : shown.length === 0 ? (
              <div className="text-center py-24 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="no-results">{t("no_results")}</div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {shown.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
